// The Research desk: the decisions table and the intake (V4Decisions), and the
// file of a decision (V4DecisionDossier).
export default {
  titre: 'Research desk',
  enumeration: '{debut} and {fin}',
  citation: '“{texte}”',
  pct: '{n}%',
  // The engine's three families, and the question each one asks.
  famille: {
    tester_concepts: { titre: 'Test concepts', question: 'Does this concept find its audience before a single euro is committed?' },
    lancer_positionner: { titre: 'Launch and position', question: 'Which space can really be won?' },
    etendre: { titre: 'Extend', question: 'How far can you stretch without breaking the core?' },
  },
  // The screen's four entry points.
  entree: {
    ab: {
      titre: 'Compare versions (A/B)',
      description: 'Two or three versions of the same thing: a title, a visual, a trailer, a hook. Each twin sees every version, in random order, then has to keep one.',
      exemple: 'E.g. between these two trailers for season 13, which one makes people most want to watch?',
    },
    concept: {
      titre: 'Test a concept',
      description: 'One concept, shown to each twin in their feed: what they would do with it, and whether they would come back. Reception only, with no head-to-head choice.',
      exemple: 'E.g. does our short series “technical archaeology” find its audience before we commit the shooting budget?',
    },
    lancer: {
      titre: 'Launch and position',
      description: 'The Research desk starts from what your audience already watches and what it is missing, then measures the reaction to your launch ideas.',
      exemple: 'E.g. we want to open an everyday cooking channel and the space is crowded: which angle can still be won?',
    },
    etendre: {
      titre: 'Extend',
      description: 'The Research desk tests the new format against the core: the wish to watch it, the wish to come back, and whether the channel stays the one people follow.',
      exemple: 'E.g. can our speedrun channel launch a “behind the scenes” format without losing those who come for the records?',
    },
  },
  statut: {
    open: 'in progress',
    decided: 'decided',
    abandoned: 'abandoned',
  },
  enjeu: {
    on_lance: 'Do we launch?',
    on_ajuste: 'Do we adjust?',
    on_abandonne: 'Do we drop it?',
    on_arbitre: 'Do we pick one?',
  },
  sousTitre: {
    dossier: 'Project {nom} · the decisions of all its brands, each with its question, its stimuli and its verdict',
    equipe: 'The team’s decision files: each one traces the question, the stimuli and the verdict',
  },
  echeance: {
    trancheeLe: 'decided on {date}',
    abandonnee: 'abandoned',
    aucune: 'no deadline',
    depassee: '{n} d overdue',
    dans: 'in {n} d',
  },
  confirmerSuppression: 'Delete the decision “{titre}”?\n\nIts stimuli and panel studies will be deleted. Projections already recorded in the register stay there: a missed projection is never erased. This cannot be undone.',
  erreur: {
    suppression: 'Could not delete',
    imageLourde: 'Image too large (8 MB max).',
    videoLourde: 'Video too large (2 GB maximum).',
    envoiVideo: 'Could not upload the video: {erreur}',
    lienYoutube: 'Only PUBLIC YouTube links are accepted. For any other file, use the upload.',
    bureauIndisponible: 'The Research desk is unavailable',
    protocoleInacheve: 'The decision file is open, but the Research desk could not finish the protocol: {erreur}',
    creation: 'Could not create',
    marqueSansDossier: 'The brand “{nom}” is not in any project: the Research desk works project by project. Add it to one from the projects page.',
    marqueIntrouvable: 'This brand cannot be found: the link may be old, or the brand belongs to another organisation.',
  },
  blocage: {
    videoEnCours: 'A video is still uploading: wait until it has arrived, otherwise the twins would not see it.',
    versionVide: '“{nom}” is empty: give it some content (a description, a link or a visual), or remove it.',
    uneVersion: 'A version',
    deuxVersions: 'A comparison needs at least two versions with content. Describe version B, paste its link or add its visual.',
    doublon: 'Two versions are called “{nom}”: the twins could not say which one they keep. Rename one of them.',
  },
  audience: {
    toutLeDossier: 'the whole project',
    laMarque: 'the brand',
    de: 'the audience of {nom}.',
    duDossier: 'the audience of the whole project, i.e. {liste}.',
    seulement: 'the audience of {liste} only.',
    seulementPas: 'the audience of {liste} only, not that of {autres}.',
  },
  liste: {
    dossiers: 'Projects →',
    ouverture: 'Opening the Research desk for this brand’s project…',
    sansDossier: 'The Research desk works project by project: choose one in the left-hand bar. A decision there asks the audience of all its brands, or of those the request targets.',
    decisions: 'Team decisions · {n} in progress',
    tri: 'sorted by deadline',
    colDecision: 'Decision',
    colFamille: 'Family',
    colEcheance: 'Deadline',
    colStimuli: 'Stimuli',
    colStatut: 'Status',
    chargement: 'Loading…',
    versions: 'versions',
    concept: 'concept',
    indiceSupprimer: 'Delete this decision (stimuli, panel studies). The register keeps its projections. Cannot be undone.',
    aucune: 'No decisions yet: open the first one below.',
    ouvrir: 'Open a decision',
    dossierSansMarque: 'This project has no brand yet: the Research desk asks the audience of its brands. Create one first, from the projects page.',
    plusSure: 'the safest reading',
    selectionne: 'Selected · describe it below',
    comparer: 'Compare versions',
    choisirEntree: 'Choose this option',
    decrire: 'Describe your situation',
  },
  intake: {
    titre: 'Research desk · new decision',
    fermer: 'Close',
    situation: 'Your situation, in your own words',
    ceQueFera: 'What the Research desk will do:',
    versionsEtape3: 'You will name and describe the versions in step 3.',
    exemple: 'E.g. we are unsure about launching a short series “technical archaeology” in Q4: big shooting budget, the team is split, we need a direction before the end of August…',
    enjeu: 'What is at stake:',
    reformule: 'The Research desk rephrases your request as a decision that can be settled: you approve it before any study.',
    indiceQualifier: 'The Research desk rephrases your request as a question that can be settled',
    enReformulation: 'The Research desk is rephrasing…',
    passer: 'Send to the Research desk',
  },
  miroir: {
    titre: 'The mirror: check we are looking into the right decision',
    plusProche: 'Here is the closest version as a decision:',
    multi: 'If I understand correctly, your request contains {n} separate decisions: we look into them one at a time. The first one:',
    decision: 'If I understand correctly, the decision to look into is:',
    quiRepond: 'Who answers:',
    pourquoi: 'Why: {texte}',
    pasLaBonne: 'Not the right audience? Say so in the correction below and the Research desk will reread the request.',
    partsEgales: 'Each community keeps its brand and its weight within its brand’s audience; brands carry equal weight, for lack of a comparable measure of their audience sizes.',
    famille: 'Decision family:',
    lectureBureau: 'Research desk’s reading',
    echeanceLue: '· deadline read: {date}',
    lirePlutot: 'The Research desk reads it rather as “{famille}”; you stay in control.',
    routage: 'Routing to confirm: choose the right family.',
    exempleCorrection: 'Not quite? Say what is off and the Research desk will rephrase…',
    indiceCorriger: 'Correct the rephrasing',
    reformulation: 'rephrasing…',
    corriger: 'Correct',
    revenir: '← back to the request',
    valider: 'That’s right',
  },
  stimuli: {
    titreVersions: 'The versions to compare, and by when',
    titreConcept: 'The concept to show, and by when',
    compteurComparaison: '· {n}/{max}: every twin will see them all',
    apparieeTitre: 'Paired comparison',
    apparieeTexte: 'Each twin sees every version, in random order, answers for each one, then has to keep one.',
    apparieePourquoi: 'Why this is the safest reading: in a paired design, the engine’s own bias hits each version the same way and cancels out in the gap. Orion is weakest on “what percentage” and strongest on “which of the two”.',
    receptionTitre: 'Reception measure, no head-to-head',
    receptionTexte: 'Each twin reacts to this one concept: what they would do with it in their feed, and whether they would come back. To know which one wins, add a version B: comparison is what Orion measures best.',
    ajouterB: 'Add a version B to compare',
    sansContenu: 'With nothing to show, the twins will only answer questions about their habits.',
    nomVersion: 'Version name',
    labelCourt: 'Short label',
    indiceNom: 'The name the twins will read when choosing: rename it freely',
    exempleContenuVersion: 'What the twins will see: the hook, the pitch, or a link, a visual',
    exempleContenu: 'One-sentence description',
    visuelMontre: 'Visual shown to the twins during the study',
    retirerVisuel: 'Remove the visual',
    indiceVisuel: 'Add a visual (YouTube thumbnail, concept visual…): it will be shown to the twins during the study',
    visuel: 'Visual',
    retirerVersion: 'Remove this version',
    retirerConcept: 'Remove this concept',
    videoMontree: 'Shown to the twins during the study: {url}',
    lienYoutube: 'YouTube link',
    retirerVideo: 'Remove the video',
    exempleLien: 'Public YouTube link (optional)',
    indiceLien: 'Only PUBLIC links work: the model rejects an unlisted video',
    indiceFichier: 'Upload a video file (mp4, mov, webm): 2 GB maximum',
    envoi: 'Uploading…',
    ouFichier: 'or a file',
    nomsArbitrage: 'The twins will choose on these names: prefer a name that says what the version is (“dark teaser”, “bright teaser”) to a single letter.',
    ajouterVersion: 'Add version {lettre}',
    ajouterConcept: 'Add a concept',
    territoires: 'Study territories:',
    territoiresVises: 'those carried by the targeted brands, to name the field in the questions',
    territoiresDossier: 'the project’s own, to name the field in the questions',
    echeance: 'Deadline:',
    indiceEchantillon: 'The sample is sized by coverage of your communities’ sub-pockets: it is never chosen by hand',
    echantillon: 'ⓘ calculated sample, anchored on the latest audit',
    retourMiroir: '← the mirror',
    ouvrirDossier: 'Open the decision file',
    indiceCreer: 'Opens the decision file and its protocol',
    ecritProtocole: 'The Research desk is writing the protocol…',
    ouvrirDossierDecision: 'Open the decision file',
    relance: 'The protocol can be relaunched from the decision file.',
  },
  // The file of a decision (V4DecisionDossier).
  dossier: {
    titre: 'File {ref} · {famille}',
    titreSeul: 'Decision file',
    marquePart: '{nom} ({pct}%)',
    sansPersonasUne: '{nom} has no personas yet: its audience was not asked, and the verdict does not apply to it.',
    sansPersonasPlusieurs: '{noms} have no personas yet: their audiences were not asked, and the verdict does not apply to them.',
    ouvertLe: 'opened {date}',
    echeance: 'due {date}',
    erreur: {
      chargement: 'Could not load',
      demarrage: 'The study did not start: the queue may be full. Check the jobs before relaunching.',
      generation: 'Could not generate',
      ajustement: 'Could not adjust',
      lancement: 'Could not run',
      confrontation: 'Could not record the check',
    },
    progression: {
      instanciation: 'Setting up the panel…',
      interrogation: 'Asking the twins…',
      termine: 'Done',
    },
    typeQuestion: {
      stimulus_reaction: 'reaction',
      behavior: 'actual behaviour',
      arbitrage: 'head-to-head',
    },
    confiance: {
      elevee: 'high',
      moyenne: 'medium',
      faible: 'low',
    },
    receptivite: {
      indifferent: 'indifferent',
      sceptique: 'sceptical',
      ouvert: 'open',
      enthousiaste: 'enthusiastic',
    },
    jumeau: {
      pourquoi: 'Why that choice?',
      partage: 'Who would you share it with?',
    },
    toutes: 'All decisions',
    questionClient: 'The client’s question',
    quiRepond: 'Who answers:',
    stimuli: 'Stimuli: {liste}, shown to each twin in random order.',
    visuelAgrandir: 'Visual “{nom}”: click to enlarge',
    protocole: 'Protocol · generated by the Research desk',
    verifie: 'protocol checked',
    erreurs: '{n} error | {n} errors',
    resume: '{q} questions · {n} twins',
    indiceRegles: 'Every protocol is checked automatically: no leading questions, balanced scales, opt-out options present, stimuli in random order.',
    regles: 'ⓘ 12 rules checked',
    indiceRegenerer: 'Regenerates the protocol',
    regeneration: 'regenerating…',
    regenerer: 'regenerate',
    voirProtocole: 'show the protocol',
    replier: 'collapse',
    traduitQuestion: 'The Research desk turns your question into a clean protocol (neutral wording, scales with an indifference option, randomised stimuli):',
    indiceEcrire: 'The Research desk writes the study protocol',
    ecrire: 'Write the protocol',
    indiceEchantillon: 'Sized by coverage of your communities’ sub-pockets: never chosen by hand',
    jumeauxEchantillon: 'twins · calculated sample',
    communautes: 'communities',
    stimuliAleatoire: 'stimuli · random order',
    questions: 'questions',
    indiceConcept: 'Question asked about the concept “{nom}”: each concept is seen separately, in random order',
    exempleAjuster: 'Adjust in business terms, e.g. “stress the price”, “add the family angle”…',
    indiceAjuster: 'The Research desk rewrites your request into the protocol and checks it again: questions are never edited by hand',
    enAjustement: 'the Research desk is rewriting…',
    ajuster: 'Adjust',
    echantillon: 'Sample sized by sub-pocket coverage · weighted to the communities’ measured shares',
    echantillonPartsEgales: 'Sample sized by sub-pocket coverage · weighted to the communities’ measured shares · brands carry equal weight, for lack of a comparable measure of their audience sizes',
    etudeClose: 'Study closed: to run it again, regenerate the protocol. It will be a new, dated study.',
    indiceLancer: 'Each twin answers individually, blind to the others',
    doitEtreVerifie: 'The protocol must be checked',
    lancer: 'Run the study',
    enErreur: 'Study failed: {message}',
    annulee: 'Study cancelled: the remaining twins were not asked.',
    ab: {
      titre: 'A/B verdict · paired comparison',
      net: 'clear gap',
      serre: 'narrow gap',
      arbitre: '{n} twins picked one',
      part: '{nom}: {pct} (weighted), {n} twins',
      aucuneTitre: 'No version kept, or “don’t know”: {pct}',
      aucune: 'none · don’t know',
      ecart: 'gap {a} − {b}',
      zero: 'zero: no difference',
      intervalle: 'Interval at ±2σ: from {de} to {a} pts',
      mesure: 'Measured gap: {ecart} pts ± {marge}',
      plan: 'Each twin saw every version, in random order, then kept one. In this paired design, the engine’s own bias hits each version the same way and cancels out in the gap: read the gap rather than the percentages.',
      marge: '“±” = two standard deviations, computed on {n} raw respondents; weighting is not counted, so the margin is slightly optimistic when the weights are very uneven.',
    },
    verdict: {
      titre: 'The verdict',
      confiance: '{niveau} confidence',
      pourquoi: 'ⓘ why this confidence',
      closLe: 'study closed on {date}',
      tient: 'What holds',
      fracture: 'What splits',
      prochaine: 'Next step →',
      blast: 'Open BLAST',
    },
    profilPanel: 'Profile of the panel asked',
    distributions: {
      titre: 'Distributions',
      global: 'Overall (weighted)',
      indiceRedressement: 'Weighting: answers are weighted by each community’s measured share of the real audience.',
      redresse: 'ⓘ weighted to the measured shares',
      brut: 'ⓘ raw segment, unweighted',
      adhesion: 'positive',
      rejet: 'rejection / indifference',
      nsp: 'don’t know',
      rencontrer: 'Meet twins from this segment →',
    },
    verbatims: 'Verbatims · excerpts from open answers',
    votreDecision: 'Your decision',
    exempleNote: 'The recommendation line, in your own words: the decision is human, just better informed.',
    indiceEnregistrer: 'Saves the decision note',
    enregistrer: 'Save',
    indiceTrancher: 'Records the verdict and closes the decision',
    trancher: 'Settle the decision',
    fermer: 'Close',
  },
  registre: {
    titre: 'The register · projected vs actual',
    indice: 'Each projection is timestamped and frozen at the time of the study. It is checked against reality with BLAST / analytics measurements.',
    figees: 'ⓘ projections frozen, never rewritten',
    colPrediction: 'Projection',
    colPredit: 'Projected',
    colReel: 'Actual',
    colEcart: 'Gap',
    colStatut: 'Status',
    figeeLe: 'frozen on {date}',
    confronteeLe: 'checked on {date}',
    confiance: 'conf. {niveau}',
    directionnel: 'directional',
    exempleFait: 'the observed fact…',
    indiceConfronter: 'Records the fact and the gap: a check is never rewritten',
    confronter: 'Check',
    ecart: {
      confirme: 'confirmed',
      nuance: 'qualified',
      infirme: 'refuted',
    },
    statut: {
      observed: 'checked',
      pending: 'awaiting actuals',
      abandoned: 'abandoned',
    },
  },
}
